import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { 
  Star,
  Send,
  CheckCircle2,
  Quote,
  MessageSquare,
  Users
} from "lucide-react";
import SEO, { pageSEO } from "../components/SEO";
import MainLayout from "../components/MainLayout";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Star Rating Component
const StarRating = ({ rating, setRating, interactive = false }) => {
  const [hover, setHover] = useState(0);
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type={interactive ? "button" : undefined}
          onClick={() => interactive && setRating(star)}
          onMouseEnter={() => interactive && setHover(star)}
          onMouseLeave={() => interactive && setHover(0)}
          className={`${interactive ? "cursor-pointer" : "cursor-default"} transition-transform ${interactive && hover >= star ? "scale-110" : ""}`}
          disabled={!interactive}
        >
          <Star
            className={`w-6 h-6 ${
              (interactive ? (hover || rating) : rating) >= star
                ? "fill-yellow-400 text-yellow-400"
                : "text-slate-600"
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Review Submission Form
const ReviewForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    title: "",
    message: "",
    rating: 5
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.rating < 1) {
      toast.error("Please select a star rating");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/reviews`, formData);
      setSubmitted(true);
      toast.success("Thank you for your review!");
      onSuccess && onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit review");
    }
    setLoading(false);
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-2xl border border-green-500/20 p-8 text-center"
      >
        <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8 text-green-400" />
        </div>
        <h3 className="text-xl font-bold text-white mb-2">Thank You!</h3>
        <p className="text-slate-400">
          Your review has been submitted and will be visible after approval.
          We appreciate your feedback!
        </p>
        <button
          onClick={() => {
            setSubmitted(false);
            setFormData({ name: "", email: "", title: "", message: "", rating: 5 });
          }}
          className="mt-4 text-blue-400 hover:text-blue-300 text-sm"
        >
          Submit another review
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">Your Name *</label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50"
            placeholder="John Smith"
            data-testid="review-name"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">Email Address *</label>
          <input
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50"
            placeholder="john@example.com"
            data-testid="review-email"
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">Review Title *</label>
        <input
          type="text"
          required
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50"
          placeholder="Great service!"
          data-testid="review-title"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">Your Review *</label>
        <textarea
          required
          rows={4}
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50 focus:ring-1 focus:ring-blue-500/50 resize-none"
          placeholder="Tell us about your experience with SmartIT..."
          data-testid="review-message"
        />
      </div>
      
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-3">Your Rating *</label>
        <StarRating rating={formData.rating} setRating={(r) => setFormData({ ...formData, rating: r })} interactive />
      </div>
      
      <button
        type="submit"
        disabled={loading}
        className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold py-3 px-6 rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
        data-testid="review-submit"
      >
        {loading ? (
          <>
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            <Send className="w-5 h-5" />
            Submit Review
          </>
        )}
      </button>
    </form>
  );
};

// Testimonial Card Component (for Bark reviews)
const TestimonialCard = ({ name, summary, rating = 5, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 rounded-2xl border border-slate-700/50 p-6 hover:border-blue-500/30 transition-all"
    >
      {/* Stars Header */}
      <div className="flex items-center gap-2 mb-4">
        <div className="flex">
          {[...Array(rating)].map((_, i) => (
            <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
          ))}
        </div>
        <span className="text-xs text-slate-400 font-medium">Verified Client Review</span>
      </div>
      
      {/* Quote */}
      <div className="relative">
        <Quote className="absolute -top-2 -left-2 w-8 h-8 text-blue-500/20" />
        <p className="text-slate-300 leading-relaxed pl-4 italic">
          "{summary}"
        </p>
      </div>
      
      {/* Client Name */}
      <div className="mt-4 pt-4 border-t border-slate-700/50 flex items-center">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
            <span className="text-white font-bold text-sm">
              {name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
            </span>
          </div>
          <div>
            <p className="text-white font-medium">{name}</p>
            <p className="text-slate-500 text-xs">SmartIT Client</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// User Submitted Review Card
const UserReviewCard = ({ review, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="bg-gradient-to-br from-slate-800/40 to-slate-900/40 rounded-2xl border border-slate-700/50 p-6"
    >
      <div className="flex items-center gap-2 mb-3">
        <StarRating rating={review.rating} />
      </div>
      <h4 className="text-white font-semibold mb-2">{review.title}</h4>
      <p className="text-slate-400 text-sm leading-relaxed">"{review.message}"</p>
      <div className="mt-4 pt-3 border-t border-slate-700/30">
        <p className="text-slate-500 text-sm">— {review.name}</p>
      </div>
    </motion.div>
  );
};

const ReviewsPage = () => {
  const [userReviews, setUserReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);

  // Verified Bark testimonials with provided summaries
  const barkTestimonials = [
    {
      name: "PC Build Client",
      summary: "SmartIT repaired and optimised my PC, replaced the necessary parts and provided on-site support. Fast, reliable and highly professional service."
    },
    {
      name: "Aiah Bondowa T.",
      summary: "Excellent communication and very flexible. SmartIT made the whole process easy and smooth."
    },
    {
      name: "Ella Underhill",
      summary: "Helpful, efficient and very kind. I would recommend SmartIT to everyone."
    },
    {
      name: "Ella Underhill",
      summary: "Quick response and extremely fast resolution. Fair pricing and very professional service."
    },
    {
      name: "Geoff Day",
      summary: "SmartIT restored my crashed desktop the same day and at a much better price than mainstream shops. My go-to IT service from now on."
    },
    {
      name: "David Borrett",
      summary: "Clear instructions and instant results. Simple, effective and reliable support from SmartIT."
    },
    {
      name: "Maryam Shah",
      summary: "Very fast service, patient and understanding throughout. My laptop looks brand new. Highly recommend SmartIT — 10/10."
    },
    {
      name: "Bina Shah",
      summary: "Quick response, home visit included, and even offered a replacement drive after data recovery. Outstanding service from SmartIT."
    },
    {
      name: "Niamh Burton",
      summary: "SmartIT repaired my Chromebook quickly and replaced the battery. Efficient, great value and very professional."
    },
    {
      name: "Lottie Mander",
      summary: "Problem solved within a couple of days and everything explained clearly at collection. Highly recommended SmartIT."
    },
    {
      name: "Lisa Wilde",
      summary: "Amazing service at a very reasonable price. Great communication and very knowledgeable. Highly recommend SmartIT for all IT needs."
    }
  ];

  useEffect(() => {
    fetchUserReviews();
  }, []);

  const fetchUserReviews = async () => {
    try {
      const res = await axios.get(`${API}/reviews`);
      setUserReviews(res.data);
    } catch (err) {
      console.error("Failed to load reviews:", err);
    }
    setLoadingReviews(false);
  };

  // Schema.org Review markup
  const reviewSchema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "SmartIT - Laptop & PC Repair Aylesbury",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "reviewCount": String(barkTestimonials.length + userReviews.length),
      "bestRating": "5",
      "worstRating": "1"
    },
    "review": barkTestimonials.map(t => ({
      "@type": "Review",
      "reviewRating": {
        "@type": "Rating",
        "ratingValue": "5"
      },
      "author": {
        "@type": "Person",
        "name": t.name
      },
      "reviewBody": t.summary
    }))
  };

  return (
    <MainLayout>
      <div data-testid="reviews-page">
        <SEO 
          title="SmartIT Reviews – What Clients Say About Our IT Services"
          description="Read verified client reviews for SmartIT laptop repair and PC repair services in Aylesbury. See why customers rate us 5 stars for computer repair, SSD upgrades, and IT support."
          keywords="SmartIT reviews, laptop repair Aylesbury reviews, PC repair reviews, computer repair testimonials, IT support Aylesbury feedback"
          url="https://thesmartit.co.uk/reviews"
          image="https://thesmartit.co.uk/og-image.png"
          schema={reviewSchema}
        />

        {/* Header */}
        <div className="bg-gradient-to-b from-slate-900 to-slate-950 border-b border-slate-800/50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                <Users className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold text-white">
                  Client Reviews
                </h1>
                <p className="text-slate-400 mt-1">What our customers say about SmartIT</p>
              </div>
            </div>

            {/* Rating Summary */}
            <div className="flex items-center gap-4 mt-6 p-4 bg-slate-800/30 rounded-xl border border-slate-700/30 inline-flex">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <div>
                <span className="text-white font-bold text-xl">5.0</span>
                <span className="text-slate-400 text-sm ml-2">Based on {barkTestimonials.length}+ verified reviews</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Review Submission Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-16"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-blue-400" />
            </div>
            <h2 className="text-2xl font-bold text-white">Leave a Review</h2>
          </div>
          
          <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-2xl border border-slate-700/50 p-6 sm:p-8">
            <p className="text-slate-400 mb-6">
              Had a great experience with SmartIT? We'd love to hear from you! Your feedback helps us improve and helps others find reliable IT support.
            </p>
            <ReviewForm onSuccess={fetchUserReviews} />
          </div>
        </motion.section>

        {/* User Submitted Reviews */}
        {userReviews.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="mb-16"
          >
            <h2 className="text-2xl font-bold text-white mb-6">Recent Reviews</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {userReviews.map((review, index) => (
                <UserReviewCard key={review.id} review={review} index={index} />
              ))}
            </div>
          </motion.section>
        )}

        {/* Verified Bark Testimonials */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Verified Client Reviews</h2>
              </div>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {barkTestimonials.map((testimonial, index) => (
              <TestimonialCard
                key={index}
                name={testimonial.name}
                summary={testimonial.summary}
                index={index}
              />
            ))}
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-16 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-2xl border border-blue-500/20 p-8 text-center"
        >
          <h3 className="text-2xl font-bold text-white mb-3">
            Ready to Experience Our Service?
          </h3>
          <p className="text-slate-400 mb-6 max-w-xl mx-auto">
            Join our satisfied customers. Get your laptop or PC fixed with a FREE diagnostic.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/diagnostic"
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-semibold px-6 py-3 rounded-xl hover:opacity-90 transition-opacity"
              data-testid="reviews-diagnostic-btn"
            >
              Run Free Diagnostic
            </Link>
            <a
              href="https://wa.me/447435324947"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-green-500 text-white font-semibold px-6 py-3 rounded-xl hover:bg-green-600 transition-colors"
              data-testid="reviews-whatsapp-btn"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              Contact on WhatsApp
            </a>
          </div>
        </motion.div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ReviewsPage;
